import cn from 'classnames'
import { base_colorAction } from 'ext-ui/style/colors'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, { ReactElement, useId } from 'react'

import style from './Stepper.style'

export type StepperStep = {
  // Already-translated label for the step. ext-ui primitives don't localise —
  // consumers pass translated strings.
  title: string
  // Optional already-translated supporting line shown beneath the title.
  subtitle?: string
  // Optional test id applied to the step's <li>. When the step is completed and
  // interactive, its button additionally gets `${testId}-nav`.
  testId?: string
}

type StepState = 'completed' | 'active' | 'upcoming'
type StepperOrientation = 'vertical' | 'horizontal'

type StepperProps = {
  steps: StepperStep[]
  // 1-based index of the active step. Steps before it render as completed,
  // steps after it as upcoming.
  currentStep: number
  // Invoked with the 1-based step number when a user activates an
  // already-completed (revisitable) step. Omit to render a display-only,
  // non-interactive stepper.
  onStepSelect?: (step: number) => void
  // Already-translated heading rendered above the list (e.g. "Get Started").
  heading?: string
  // Heading level for the rendered heading, so the stepper slots into the host
  // page's outline correctly. Defaults to 2.
  headingLevel?: 2 | 3 | 4
  // Already-translated accessible label for the <nav> landmark.
  ariaLabel?: string
  // Builds the accessible label for a completed, tappable step from its title
  // (e.g. (title) => `Go back to ${title}`). Falls back to the bare title.
  getStepNavLabel?: (title: string) => string
  // Test id applied to the <ol> list.
  testId?: string
  orientation?: StepperOrientation
  className?: string
}

function getStepState(stepNumber: number, currentStep: number): StepState {
  if (stepNumber < currentStep) return 'completed'
  if (stepNumber === currentStep) return 'active'
  return 'upcoming'
}

function Stepper(props: StepperProps): ReactElement {
  const {
    steps,
    currentStep,
    onStepSelect,
    heading,
    headingLevel = 2,
    ariaLabel,
    getStepNavLabel,
    testId,
    orientation = 'vertical',
    className = '',
  } = props

  const HeadingTag = `h${headingLevel}` as 'h2' | 'h3' | 'h4'

  // Name the navigation landmark. An explicit ariaLabel wins; otherwise fall
  // back to the visible heading (via aria-labelledby) so the landmark is never
  // left ambiguous on a page with multiple navs.
  const headingId = useId()
  const hasHeading = heading !== undefined
  const navLabelledBy =
    ariaLabel === undefined && hasHeading ? headingId : undefined

  return (
    <nav
      className={cn('Stepper', className)}
      data-orientation={orientation}
      css={style}
      aria-label={ariaLabel}
      aria-labelledby={navLabelledBy}
    >
      {hasHeading && (
        <HeadingTag id={headingId} className="Stepper-heading">
          {heading}
        </HeadingTag>
      )}
      <ol className="Stepper-list" data-testid={testId}>
        {steps.map(({ title, subtitle, testId: stepTestId }, index) => {
          const stepNumber = index + 1
          const state = getStepState(stepNumber, currentStep)
          const isCompleted = state === 'completed'
          const isLast = index === steps.length - 1
          const isInteractive = isCompleted && onStepSelect !== undefined
          const showVerticalConnector = orientation === 'vertical' && !isLast
          const showHorizontalConnector =
            orientation === 'horizontal' && !isLast

          const rail = (
            <span className="Stepper-rail">
              <span className="Stepper-badge" aria-hidden="true">
                {isCompleted ? (
                  <CheckmarkIcon
                    width={16}
                    height={16}
                    fill={base_colorAction}
                  />
                ) : (
                  stepNumber
                )}
              </span>
              {showVerticalConnector && (
                <span className="Stepper-connector" aria-hidden="true" />
              )}
            </span>
          )

          const content = (
            <span className="Stepper-content">
              <span className="Stepper-title">{title}</span>
              {subtitle !== undefined && (
                <span className="Stepper-subtitle">{subtitle}</span>
              )}
            </span>
          )

          return (
            <li
              key={stepTestId ?? stepNumber}
              className="Stepper-item"
              data-state={state}
              data-testid={stepTestId}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              {isInteractive ? (
                <button
                  type="button"
                  className="Stepper-row"
                  onClick={(): void => onStepSelect(stepNumber)}
                  aria-label={getStepNavLabel ? getStepNavLabel(title) : title}
                  data-testid={stepTestId ? `${stepTestId}-nav` : undefined}
                >
                  {rail}
                  {content}
                </button>
              ) : (
                <div className="Stepper-row">
                  {rail}
                  {content}
                </div>
              )}
              {showHorizontalConnector && (
                <span className="Stepper-connector" aria-hidden="true" />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

export default Stepper
